import Vue from "vue";
import router from "@/router";
import RecordsService from "@/services/records.service";
import modelCache from "@/services/modelCache.service";
import { VueOfflineStorage } from "vue-offline";
import { handleError } from "@/functions/handleError.function";
import { MutateFilter } from "@/services/filters.service";

// initial state
const state = {
  records: {},
  items: {},
  recordsList: [],
  ownRecords: [],
  openRecords: [],
  scannedRecord: {},
  scannedRecordList: [],
  scannedRecordTypes: [],
  scannedRecordTags: [],
  file: [],
  fileIndex: [],
  tasks: [],
  competencies: [],
  markers: [],
  reasons: [],
  serverData: {},
  loading: {
    Records: false,
    ownRecords: false,
    openRecords: false,
    success: false,
  },
  filters: {},
  pages: {},
  scannedRecordFilters: {},
};

// getters
const getters = {
  records: state => {
    return state.records;
  },
  items: state => recordId => {
    return state.items[recordId];
  },
  recordsList: state => {
    return state.recordsList;
  },
  openRecords: state => {
    return state.openRecords;
  },
  getScannedRecord: state => id => {
    return state.scannedRecordList.find(x => x.id = id);
  },
  scannedRecordList: state => {
    return state.scannedRecordList;
  },
  scannedRecordTypes: state => {
    return state.scannedRecordTypes;
  },
  scannedRecordTags: state => {
    return state.scannedRecordTags;
  },
  file: state => {
    return state.file;
  },
  fileIndex: state => {
    return state.fileIndex;
  },
  ownRecords: state => {
    return state.ownRecords;
  },
  tasks: state => {
    return state.tasks;
  },
  competencies: state => {
    return state.competencies;
  },
  markers: state => {
    return state.markers;
  },
  reasons: state => {
    return state.reasons;
  },
  serverData: state => key => {
    return state.serverData[key];
  },
  loading: state => {
    return state.loading;
  },
  search: state => {
    return state.filters.search;
  },
  filters: state => {
    return state.filters;
  },
  pages: state => {
    return state.pages;
  },
  scannedRecordFilters: state => {
    return state.scannedRecordFilters;
  },
  scannedRecordPages: state => {
    return state.scannedRecordPages;
  },
};
// actions (async)
const actions = {
  getRecords(context, query) {
    state.loading.Records = true;
    if (!query) {
      query = state.filters;
    }
    RecordsService.list(query)
      .then(function(response) {
        state.loading.Records = false;
        state.recordsList = response.data.results;
        let pages = Object.assign({}, response.data);
        delete pages.results;
        Vue.set(state.pages, "recordList", pages);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  createRecord(context, payload) {
    const redirect = payload.redirect;
    delete payload.redirect;
    RecordsService.create(payload)
      .then(function(response) {
        if (redirect == "record-detail") {
          router.push({
            name: "record-detail",
            params: { id: response.data.id }
          })
        } else {
          router.push({ name: "record-index" });
        };
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getRecord(context, payload) {
    /* payload:
     * - id
     * - isOnline
     */

    /* Get record from storage if DNE in the store (cache first) */
    let cache = VueOfflineStorage.get("records");
    if (cache) {
      cache = cache[payload.id];
    }
    // Initialize from the cache if the record DNE
    if (cache && !state.records[payload.id]) {
      context.commit("setRecord", cache);
    }
    if (payload.isOnline) {
      RecordsService.get(payload.id)
        .then(function(response) {
          if (cache && !response.data.is_closed) {
            // Check and manage version conflicts
            let serverModified = Date.parse(response.data.modified);
            let cacheModified = Date.parse(cache.modified);
            let syncState = undefined;
            // Records can *ONLY* be closed on the server. So if the server is
            // closed then ALWAYS use that model.
            if (response.data.is_closed == false) {
              context.commit("setRecord", response.data);
            } else if (serverModified != cacheModified) {
              syncState = serverModified > cacheModified ? "server" : "cache";
            } else {
              syncState = false;
            }
            Vue.set(state.records[payload.id], "sync", syncState);
            if (syncState) {
              context.commit("setServerData", {
                key: `record-${payload.id}`,
                data: response.data
              });
            }
          } else {
            context.commit("setRecord", response.data);
          }
        })
        .catch(function(error) {
          var data = handleError(error);
          context.commit("core/setError", data, { root: true });
        });
    }
  },
  updateRecord(context, payload) {
    context.commit("core/clearErrors", {}, { root: true });
    return RecordsService.update(payload)
      .then(function(response) {
        context.commit("setRecord", response.data);
        return true;
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
        return false;
      });
  },
  getItems(context, payload) {
    /* payload:
     * - id: The id of the associated record
     * - isOnline: Online state from vue-offline plugin
     */

    /* Get items from storage if the record DNE in the store (cache-first) */
    let cache = VueOfflineStorage.get("items");
    if (cache) {
      cache = cache[payload.id];
    }
    if (cache && !state.items[payload.id]) {
      context.commit("setItems", {
        id: payload.id,
        data: cache
      });
    }
    // If online get udpated data from the server
    if (payload.isOnline) {
      RecordsService.getItems(payload.id)
        .then(function(response) {
          var record = state.records[payload.id];
          if (cache && record && record.is_closed == false) {
            // Check and manage version conflicts
            let syncState = false; // Track record sync for ALL items
            for (let item of response.data) {
              let serverModified = Date.parse(item.modified);
              let idx = cache.findIndex(i => i.id == item.id);
              let cacheModified = Date.parse(cache[idx].modified);
              if (syncState == false && serverModified != cacheModified) {
                syncState = serverModified > cacheModified ? "server" : "cache";
              }
            }
            // Update the sync state ON THE RECORD for the component to handle
            // (Note that this covers *ALL* of the items as a single stack.)
            Vue.set(state.records[payload.id], "sync", syncState);
            if (syncState != false) {
              context.commit("setServerData", {
                key: `record-${payload.id}-items`,
                data: response.data
              });
            }
          } else {
            for (let item of response.data) {
              // Initialize a reason field for use in the RecordComplete
              // component.
              item.reason_error = false;
            }
            context.commit("setItems", {
              id: payload.id,
              data: response.data
            });
          }
        })
        .catch(function(error) {
          var data = handleError(error);
          context.commit("core/setError", data, { root: true });
        });
    }
  },
  updateItem(context, payload) {
    context.commit("core/clearErrors", {}, { root: true });
    return new Promise((resolve, reject) => {
      // Update server
      RecordsService.updateItem(payload)
        .then(function(response) {
          let items = state.items[payload.record];
          let idx = items.findIndex(i => i.id == response.data.id);
          // Iterate through the object to avoid overwriting other props set in
          // the component layer (e.g. error status)
          for (let key of Object.keys(response.data)) {
            items[idx][key] = response.data[key];
          }
          context.commit("setItems", {
            id: payload.record,
            data: items
          });
          // Resolve the Promise
          resolve(response);
        })
        .catch(function(error) {
          var data = handleError(error);
          context.commit("core/setError", data, { root: true });
          // Update the cached model
          modelCache.set("items", payload.id, payload);
          // Reject the Promise
          reject(error);
        });
    });
  },
  createItem(context, payload) {
    context.commit("core/clearErrors", {}, { root: true });
    RecordsService.createItem(payload)
      .then(function(response) {
        // Copy then set the new item using a mutation in order to invoke the
        // caching behaviour.
        var items = state.items[payload.record];
        items.push(response.data);
        items = items.sort(function(a, b) {
          if (a.competency_reference < b.competency_reference) {
            return -1;
          } else if (a.competency_reference > b.competency_reference) {
            return 1;
          } else {
            return 0;
          }
        });
        context.commit("setItems", {
          id: payload.record,
          data: items,
        });
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  destroyItem(context, payload) {
    context.commit("core/clearErrors", {}, { root: true });
    RecordsService.destroyItem(payload)
      .then(function() {
        // Copy then set the new item using a mutation in order to invoke the
        // caching behaviour.
        var items = state.items[payload.record];
        const idx = items.findIndex(i => i.id == payload.item);
        if (idx > -1) {
          items.splice(idx, 1);
        };
        context.commit("setItems", {
          id: payload.record,
          data: items,
        });
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });

  },
  getOwnRecords(context) {
    state.loading.ownRecords = true;
    let userProfile = context.rootGetters["auth/userProfile"];
    let query = {
      candidate: userProfile.id,
      is_active: true,
      is_closed: true
    };
    RecordsService.list(query)
      .then(function(response) {
        state.ownRecords = response.data.results;
        state.loading.ownRecords = false;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getOpenRecords(context) {
    state.loading.openRecords = true;
    let userProfile = context.rootGetters["auth/userProfile"];
    let query = {
      instructor: userProfile.id,
      is_closed: false
    };
    RecordsService.list(query)
      .then(function(response) {
        state.loading.openRecords = false;
        state.openRecords = response.data.results;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getFile(context, payload) {
    // Clear any old data from the store.
    Vue.set(state, "file", []);
    RecordsService.getFile(payload.id)
      .then(function(response) {
        state.file = response.data;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  listFiles(context) {
    RecordsService.listFiles()
      .then(function(response) {
        state.fileIndex = response.data;
      })
      .catch(function(error) {
        var data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getTasks(context) {
    RecordsService.listTasks()
      .then(function(response) {
        state.tasks = response.data;
        VueOfflineStorage.set("tasks", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getCompetencies(context) {
    RecordsService.listCompetencies()
      .then(function(response) {
        state.competencies = response.data;
        VueOfflineStorage.set("competencies", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getMarkers(context) {
    // Get the marker codes for completing training records
    RecordsService.listMarkers()
      .then(function(response) {
        state.markers = response.data;
        VueOfflineStorage.set("markers", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getReasonCodes(context) {
    // Get the marker codes for completing training records
    RecordsService.listReasonCodes()
      .then(function(response) {
        state.reasons = response.data;
        VueOfflineStorage.set("reasons", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  signRecord(context, payload) {
    RecordsService.signExam(payload)
      .then(function(response) {
        const sigField = payload.role;
        const dtField = payload.role + "_dt";
        Vue.set(state.records[payload.id], sigField, response.data["digest"]);
        Vue.set(state.records[payload.id], dtField, response.data["timestamp"]);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  // Scanned Records (PDFs etc.)
  listScannedRecord(context, query) {
    state.loading.Records = true;
    if (!query) {
      query = state.scannedRecordFilters;
    }
    RecordsService.listScannedRecord(query)
      .then(function(response) {
        state.loading.Records = false;
        state.scannedRecordList = response.data.results;
        let pages = Object.assign({}, response.data);
        delete pages.results;
        Vue.set(state.pages, "scannedRecordList", pages);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      })
  },
  createScannedRecord(context, payload) {
    state.loading.Records = true;
    return RecordsService.createScannedRecord(payload)
      .then(function() {
        state.loading.Records = false;
        router.push({
          name: "scanned-record-index",
        });
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getScannedRecord(context, payload) {
    state.loading.Records = true;
    RecordsService.getScannedRecord(payload.id)
      .then(function(response) {
        state.loading.Records = false;
        context.scannedRecord = response.data;
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      })
  },
  updateScannedRecord(context, payload) {
    Vue.set(state.loading, "Records", true);
    Vue.set(state.loading, "success", false);
    return RecordsService.updateScannedRecord(payload)
      .then(function(response) {
        Vue.set(state.loading, "Records", false);
        Vue.set(state.loading, "success", true);
        context.scannedRecord = response.data;
      })
      .catch(function(error) {
        Vue.set(state.loading, "Records", false);
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      })
  },
  getScannedRecordTypes(context) {
    RecordsService.getScannedRecordTypes()
      .then((response) => {
        state.scannedRecordTypes = response.data;
      })
      .catch((error) => {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      })
  },
  getScannedRecordTags(context) {
    RecordsService.getScannedRecordTags()
      .then((response) => {
        state.scannedRecordTags = response.data;
      })
      .catch((error) => {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      })
  },
};

// Mutations (sync)
const mutations = {
  clearState() {
    state.records = {};
    state.items = {};
    state.ownRecords = [];
    state.openRecords = [];
    state.recordsList = [];
    state.scannedRecord = {};
    state.scannedRecordList = [];
    state.scannedRecordTypes = [],
    state.scannedRecordTags = [],
    state.tasks = [];
    state.markers = [];
    state.reasons = [];
    state.loading = {
      Records: false,
      ownRecords: false,
      openRecords: false
    };
    state.filters = {
        search: ""
    };
    state.pages = {};
    state.scannedRecordFilters = {
        search: ""
    };
  },
  setItems(state, payload) {
    Vue.set(state.items, payload.id, payload.data);
  },
  setRecord(state, payload) {
    // Add/update the 'NoHours' property from (in order or priority) the state,
    // the cache, or a default value.
    var cache = VueOfflineStorage.get("records") ?
        VueOfflineStorage.get("records")[payload.id] : undefined;
    if (state.records[payload.id] && state.records[payload.id].no_hours != undefined) {
      payload.no_hours = state.records[payload.id].no_hours;
    } else if (cache && cache.no_hours != undefined) {
      payload.no_hours = cache.no_hours;
    } else {
      payload.no_hours = false;
    }
    // Update the state
    Vue.set(state.records, payload.id, payload);
  },
  setItemDetail(state, payload) {
    /* payload:
     * - record: ID of the associated RECORD
     * - id: ID of the ITEM
     * ...
     * Rest as per normal item model.
     */
    let items = state.items[payload.record];
    let idx = items.findIndex(i => i.id == payload.id);
    for (let key of Object.keys(payload)) {
      Vue.set(items[idx], key, payload[key]);
    }
  },
  setTasks(state, payload) {
    state.tasks = payload;
  },
  setCompetencies(state, payload) {
    state.competencies = payload;
  },
  setMarkers(state, payload) {
    state.markers = payload;
  },
  setReasonCodes(state, payload) {
    state.reasons = payload;
  },
  setServerData(state, payload) {
    state.serverData[payload.key] = payload.data;
  },
  removeServerData(state, payload) {
    delete state.serverData[payload.key];
  },
  search(state, payload) {
    state.filters.search = payload.searchText;
  },
  filters(state, payload) {
    MutateFilter(state, payload);
  },
  scannedRecordFilters(state, payload) {
    // This copies the logic in 'services/filters/MutateFilter' as the name of
    // the filter is different
    for (let [key, val] of Object.entries(payload)) {
      if (!val || val.length == 0) {
        Vue.delete(state.scannedRecordFilters, key);
      } else {
        Vue.set(state.scannedRecordFilters, key, val);
      }
    }
  },
  closeSuccess(state) {
    Vue.set(state.loading, "success", false);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
